import { Mail } from 'lucide-react';

const URL_SPLIT_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
const URL_TEST_RE = /^(https?:\/\/|www\.)/i;
const URL_HTTP_RE = /^https?:\/\//i;
const TRAILING_PUNCT_RE = /[.,;:!?)\]]+$/;

const renderBio = (bio) =>
  bio.split(URL_SPLIT_RE).map((part, i) => {
    // Stable-per-render key: index + short prefix of the segment content. Bio
    // text is admin-authored and re-rendered only when it changes, so this
    // combination is unique enough for React's reconciler.
    const key = `${i}:${part.slice(0, 24)}`;
    if (URL_TEST_RE.test(part)) {
      const clean = part.replace(TRAILING_PUNCT_RE, '');
      const trail = part.slice(clean.length);
      const href = URL_HTTP_RE.test(clean) ? clean : `https://${clean}`;
      return (
        <span key={key}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline hover:no-underline break-all"
          >
            {clean}
          </a>
          {trail}
        </span>
      );
    }
    return <span key={key}>{part}</span>;
  });

/**
 * "About Me" section — photo + linkified bio + contact link.
 * Only renders when the admin has configured either an about photo or a bio.
 */
export const LandingAbout = ({ t, settings, apiBase }) => {
  if (!settings || (!settings.has_about_photo && !settings.about_bio)) return null;
  return (
    <section className="py-24 border-t border-border" id="about" data-testid="about-section">
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
        <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-12 text-center">
          {t('about.title')}
        </h2>
        <div className="flex flex-col md:flex-row gap-10 md:gap-16 items-start">
          {settings.has_about_photo && (
            <div className="shrink-0 mx-auto md:mx-0">
              <img
                src={`${apiBase}/site-settings/about-photo`}
                alt="About"
                loading="lazy"
                decoding="async"
                className="w-56 h-56 md:w-64 md:h-64 rounded-xl object-cover shadow-lg border border-border"
                data-testid="about-photo"
              />
            </div>
          )}
          <div className="flex-1 space-y-6">
            {settings.about_bio ? (
              <div className="text-[15px] leading-relaxed whitespace-pre-line" data-testid="about-bio">
                {renderBio(settings.about_bio)}
              </div>
            ) : (
              <p className="text-muted-foreground italic">{t('about.noBio')}</p>
            )}
            <div className="pt-4 border-t border-border">
              <a
                href="mailto:hello@fabioendrich.com"
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline transition-colors"
                data-testid="about-contact-link"
              >
                <Mail className="w-4 h-4" />
                hello@fabioendrich.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
